import React, { useEffect, useRef, useState } from 'react';
import './Wire.css';
import { useSocket } from '../context/SocketContext';
import ProfileBadge from '../components/ProfileBadge';
import ScoreBox from '../components/ScoreBox';
import BrandFooter from '../components/BrandFooter';
import PawMark from '../components/PawMark';

// Tap-circle paw (translucent white). Download from Figma "Tap_Circle"
//   -> museumx/frontend/public/assets/paw_white.png
const PAW_WHITE_SRC = `${process.env.PUBLIC_URL}/assets/paw_white.png`;

// While the circle is held, re-affirm the press at this cadence so the
// backend's auto-release watchdog never fires during a legitimate hold.
const TAP_HEARTBEAT_MS = 100;

/**
 * CONTENT2 — tug-of-war tap game.
 *
 * Rapid-tap reliability (iOS Chrome/Safari): the previous implementation
 * used React's synthetic onPointerDown/Up + setPointerCapture and tracked
 * the in-flight press in React state. On iOS WebKit, two failure modes
 * dropped taps during very fast tapping:
 *   (a) setPointerCapture on a captured element that is about to re-render
 *       (state change flips className) sometimes swallows the next
 *       pointerdown; iOS also occasionally fires pointercancel between
 *       fast taps and never releases the capture cleanly.
 *   (b) React's synthetic touch listeners are registered passive — we
 *       can't preventDefault to suppress the synthesized mouse events,
 *       which then race the next real touch and the pair gets mis-paired.
 *
 * Fix: native touch + mouse listeners attached via addEventListener with
 * {passive:false}, no pointer capture, no React state on the hot path. A
 * Set of active touch identifiers is the source of truth: the press edge
 * is sent when the Set goes 0→≥1, the release edge when it goes ≥1→0.
 * This correctly handles multi-finger, interleaved fast taps, and partial
 * touchcancel storms. React state is only used for the visual `.pressed`
 * class — visual lag there can't drop a tap.
 */
export default function Wire() {
  const { userInfo, team, score, sendTap, socket } = useSocket();

  const ci = Number(userInfo.clientIndex);
  const effectiveTeam = team != null
    ? team
    : (Number.isFinite(ci) ? ci % 2 : 0);
  const isGreen = effectiveTeam === 1;

  const [pressed, setPressed] = useState(false);
  const [beatKey, setBeatKey] = useState(0);

  const circleRef = useRef(null);
  const activeTouchIdsRef = useRef(new Set());
  const isPressedRef = useRef(false);
  const heartbeatRef = useRef(null);
  // True once any touch event has fired on this device — used to ignore
  // synthesized mouse events that follow touch sequences on iOS.
  const sawTouchRef = useRef(false);

  // `beat` from TD -> pulse ring
  useEffect(() => {
    if (!socket) return;
    const onBeat = () => setBeatKey((k) => k + 1);
    socket.on('beat', onBeat);
    return () => socket.off('beat', onBeat);
  }, [socket]);

  // --- press / release edge primitives (idempotent) ---
  // Keep these inside the effect via refs so they always see the latest
  // sendTap closure without needing to re-attach listeners.
  const sendTapRef = useRef(sendTap);
  useEffect(() => { sendTapRef.current = sendTap; }, [sendTap]);

  useEffect(() => {
    const el = circleRef.current;
    if (!el) return;
    // Locally captured Set reference for the cleanup path — satisfies
    // react-hooks/exhaustive-deps and is identity-stable for this effect.
    const activeTouches = activeTouchIdsRef.current;

    const startPress = () => {
      if (isPressedRef.current) return;
      isPressedRef.current = true;
      setPressed(true);
      // Fire the press edge IMMEDIATELY via the latest socket sender.
      try { sendTapRef.current(1); } catch {}
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      heartbeatRef.current = setInterval(() => {
        try { sendTapRef.current(1); } catch {}
      }, TAP_HEARTBEAT_MS);
    };

    const endPress = () => {
      if (!isPressedRef.current) return;
      isPressedRef.current = false;
      setPressed(false);
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      try { sendTapRef.current(0); } catch {}
    };

    const addTouches = (touchList) => {
      for (const t of touchList) activeTouches.add(t.identifier);
    };
    const removeTouches = (touchList) => {
      for (const t of touchList) activeTouches.delete(t.identifier);
    };

    const onTouchStart = (e) => {
      // Suppress synthetic mouse events + double-tap zoom heuristics.
      // This is THE critical reason for using a native non-passive
      // listener instead of React's onTouchStart (which is passive).
      e.preventDefault();
      sawTouchRef.current = true;
      addTouches(e.changedTouches);
      if (activeTouches.size > 0) startPress();
    };

    const onTouchEnd = (e) => {
      e.preventDefault();
      removeTouches(e.changedTouches);
      if (activeTouches.size === 0) endPress();
    };

    const onTouchCancel = (e) => {
      // iOS fires pointercancel/touchcancel during system gesture
      // recognition (e.g. scroll edge, multi-touch zoom). Treat as a
      // release for the cancelled touches; if all are gone, end the press.
      removeTouches(e.changedTouches);
      if (activeTouches.size === 0) endPress();
    };

    // Mouse fallback for desktop / non-touch testing. Ignored once we've
    // seen a touch (iOS replays touches as mouse events ~300ms later).
    const onMouseDown = (e) => {
      if (sawTouchRef.current) return;
      if (e.button !== 0) return;
      startPress();
    };
    const onMouseUp = (e) => {
      if (sawTouchRef.current) return;
      if (e.button !== 0) return;
      endPress();
    };
    const onMouseLeave = () => {
      if (sawTouchRef.current) return;
      endPress();
    };

    // The element itself owns touchstart so preventDefault works. Touchend
    // / cancel are attached on window so a release that drifts off-circle
    // still releases the press (iOS sometimes routes the trailing event
    // away from the original target on fast multi-tap).
    el.addEventListener('touchstart', onTouchStart, { passive: false });
    window.addEventListener('touchend', onTouchEnd, { passive: false });
    window.addEventListener('touchcancel', onTouchCancel, { passive: false });
    el.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    el.addEventListener('mouseleave', onMouseLeave);

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchCancel);
      el.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      el.removeEventListener('mouseleave', onMouseLeave);
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      // Safety release on unmount so a held press can't get stuck.
      if (isPressedRef.current) {
        isPressedRef.current = false;
        try { sendTapRef.current(0); } catch {}
      }
      activeTouches.clear();
    };
  }, []);

  // If the tab is hidden mid-press, release immediately. Without this,
  // iOS suspends events while backgrounded and TD would see a stuck press.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== 'visible' && isPressedRef.current) {
        isPressedRef.current = false;
        setPressed(false);
        if (heartbeatRef.current) {
          clearInterval(heartbeatRef.current);
          heartbeatRef.current = null;
        }
        try { sendTapRef.current(0); } catch {}
        activeTouchIdsRef.current.clear();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  return (
    <div className={`wire-screen ${isGreen ? 'green' : 'red'}`}>
      <div className="wire-inner">
        <div className="wire-badge">
          <ProfileBadge
            index={userInfo.profileIndex}
            src={userInfo.profileImage}
            nickname={userInfo.nickname}
          />
        </div>

        <div className="wire-team-banner">
          {isGreen ? 'Green Team' : 'Red Team'}
        </div>

        <div className="wire-circle-area">
          <div
            ref={circleRef}
            className={`wire-circle ${pressed ? 'pressed' : ''}`}
          >
            {beatKey > 0 && <span key={beatKey} className="wire-beat-ring" />}
            <PawMark
              src={PAW_WHITE_SRC}
              className="wire-paw"
              fallbackColor="rgba(255,255,255,0.6)"
            />
          </div>
        </div>

        <div className="wire-instruction">
          <span className="ko">원을 탭해주세요</span>
          <span className="en">Tap the Circle</span>
        </div>

        <div className="wire-score">
          <ScoreBox value={score.total} />
        </div>
      </div>
      <BrandFooter />
    </div>
  );
}
